import type { RecipeImageUpload } from '@repo/types';

// Mirrors recipes/api.ts: same-origin fetch (Vite proxies /images + /api in dev,
// CloudFront routes them in production); the endpoint is admin-only, so the
// session cookie rides along.

/** Longest edge sent over the wire; the API caps the stored size itself. */
const MAX_UPLOAD_EDGE = 2400;
const JPEG_QUALITY = 0.85;
/** Types the API accepts as-sent; anything else gets re-encoded first. */
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Upload a recipe photo: downscale huge camera files in the browser (a 12 MP
 * phone shot becomes ~1 MB), send the raw bytes to POST /api/images, and
 * resolve to the stored /images/… URL for CreateRecipeInput.imageURL.
 */
export async function uploadRecipeImage(file: Blob): Promise<string> {
  const body = await toUploadBlob(file);
  const res = await fetch('/api/images', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': body.type },
    body,
  });
  if (!res.ok) throw new Error(await readError(res));
  const uploaded: RecipeImageUpload = await res.json();
  return uploaded.imageURL;
}

/** The server's { error } message when present; a generic status line otherwise. */
async function readError(res: Response): Promise<string> {
  try {
    const body: { error?: string } = await res.json();
    if (body.error) return body.error;
  } catch {
    // Non-JSON body (proxy error page etc.) — fall through to the status line.
  }
  return `The upload failed (${res.status}).`;
}

/**
 * Downscale/re-encode in the browser so uploads stay small. Files already small
 * and in an accepted format pass through untouched (their EXIF orientation is
 * applied server-side); everything else — oversized photos, HEIC on Safari,
 * anything exotic the browser can decode — is drawn to a canvas and re-encoded
 * as JPEG, which bakes the orientation in. JPEG rather than WebP because
 * Safari's canvas cannot encode WebP; the server re-encodes to WebP anyway.
 */
async function toUploadBlob(file: Blob): Promise<Blob> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error('That file is not an image this browser can read — try a JPEG or PNG.');
  }

  const scale = Math.min(1, MAX_UPLOAD_EDGE / Math.max(bitmap.width, bitmap.height));
  if (scale === 1 && ACCEPTED_TYPES.includes(file.type)) {
    bitmap.close();
    return file;
  }

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not prepare the image for upload.');
  // JPEG has no alpha channel; matte transparency white instead of letting it go black.
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
  );
  if (!blob) throw new Error('Could not prepare the image for upload.');
  return blob;
}
